import type { SupportedLocale } from "./index.js";

const japanesePageTitles: Readonly<Record<string, string>> = {
  introduction: "はじめに",
  architecture: "アーキテクチャ",
  "getting-started": "スタートガイド",
  "installing-macos-bridge": "BridgeとmacOSアプリの導入",
  "configuring-mcp-client": "MCPクライアントの設定",
  "creating-app": "BrowserMCPアプリの作成",
  tools: "Tool",
  resources: "Resource",
  prompts: "Prompt",
  "results-errors": "結果とエラー",
  "connection-lifecycle": "接続ライフサイクル",
  "bridge-protocol": "Bridge Protocol",
  "security-model": "セキュリティモデル",
  "multiple-applications": "複数アプリケーション",
  "api-reference": "APIリファレンス",
  "bridge-configuration": "Bridge設定",
  troubleshooting: "トラブルシューティング",
  development: "開発",
  roadmap: "ロードマップ",
};

const japaneseSectionTitles: Readonly<Record<string, string>> = {
  "introduction/problem": "BrowserMCPが解決する課題",
  "introduction/browser-runtime": "ブラウザがMCPランタイムになる",
  "introduction/one-client-entry": "MCPクライアント設定は1件",
  "architecture/components": "コアコンポーネントと任意のmacOSシェル",
  "architecture/responsibility-boundaries": "責務境界",
  "architecture/request-flow": "要求フロー",
  "getting-started/prerequisites": "前提条件",
  "getting-started/workspace-setup": "ワークスペースの導入と検証",
  "getting-started/first-round-trip": "最初の往復呼び出し",
  "installing-macos-bridge/build": "クロスプラットフォームBridgeのビルドと起動",
  "installing-macos-bridge/start": "macOSネイティブアプリのビルドと利用",
  "installing-macos-bridge/pair-site": "ブラウザOriginのペアリング",
  "configuring-mcp-client/client-entry": "共通Bridgeを追加",
  "configuring-mcp-client/verify": "初期化と機能検出を確認",
  "configuring-mcp-client/client-trust": "クライアント側の承認",
  "creating-app/minimal-app": "アプリを作成して接続",
  "creating-app/static-hosting": "静的ホスティングを利用",
  "creating-app/registration-lifecycle": "登録ライフサイクルを管理",
  "tools/register-tool": "Toolを登録",
  "tools/tool-safety": "安全なToolを設計",
  "tools/site-tools": "このサイトが公開するTool",
  "resources/register-resource": "Resourceを登録",
  "resources/site-resources": "ドキュメントResource",
  "resources/resource-errors": "Resourceエラー",
  "prompts/register-prompt": "Promptを登録",
  "prompts/site-prompts": "このサイトが公開する開発支援Prompt",
  "prompts/prompt-safety": "Promptの安全性",
  "results-errors/success-results": "成功結果",
  "results-errors/errors": "実行エラーとプロトコルエラー",
  "results-errors/timeout-cancel": "タイムアウトとキャンセル",
  "connection-lifecycle/state-machine": "接続状態機械",
  "connection-lifecycle/reconnect": "安全な再接続",
  "connection-lifecycle/disconnect": "切断とクリーンアップ",
  "bridge-protocol/separation": "標準MCPとの分離",
  "bridge-protocol/envelope": "エンベロープと識別子",
  "bridge-protocol/handshake": "ハンドシェイクとネゴシエーション",
  "bridge-protocol/validation": "メッセージ検証",
  "security-model/trust-boundaries": "信頼境界",
  "security-model/pairing": "Originに結び付くペアリング",
  "security-model/input-limits": "上限と拒否",
  "security-model/known-boundaries": "既知のセキュリティ境界",
  "multiple-applications/identity": "アプリケーションとランタイムの識別",
  "multiple-applications/namespacing": "名前と競合",
  "multiple-applications/runtime-selection": "ランタイムの選択と解除",
  "api-reference/browsermcp": "BrowserMCPクラス",
  "api-reference/registration-types": "Tool、Resource、Promptの定義",
  "api-reference/connection-types": "接続型と診断型",
  "api-reference/protocol-types": "プロトコル型は内部API",
  "bridge-configuration/network": "ネットワークエンドポイント",
  "bridge-configuration/https-site": "HTTPS公開サイトとローカルWSS",
  "bridge-configuration/limits": "要求上限",
  "bridge-configuration/secrets-logs": "ペアリングとログ設定",
  "troubleshooting/diagnostic-order": "診断順序",
  "troubleshooting/bridge-not-connected": "Bridgeに接続できない",
  "troubleshooting/origin-error": "Originが拒否された",
  "troubleshooting/authentication-error": "認証に失敗した",
  "troubleshooting/name-conflict": "機能名の競合",
  "troubleshooting/version-mismatch": "プロトコルバージョンの不一致",
  "development/workspace": "ワークスペース構成",
  "development/site-architecture": "サイトのアーキテクチャ",
  "development/github-pages-build": "GitHub Pagesサブパス向けビルド",
  "development/quality-gates": "品質ゲート",
  "development/contributing": "コントリビューション規約",
  "roadmap/implemented": "このリポジトリで実装済み",
  "roadmap/verification-status": "実環境での検証状況",
  "roadmap/distribution": "配布と対応プラットフォームの拡大",
  "roadmap/security-hardening": "今後のセキュリティ強化",
  "roadmap/known-constraints": "既知の制約",
};

const localizedPageTitles: Readonly<Record<string, Readonly<Record<string, string>>>> = {
  ja: japanesePageTitles,
  "zh-CN": {
    introduction: "简介",
    architecture: "架构",
    "getting-started": "入门指南",
    "installing-macos-bridge": "安装 Bridge 与 macOS 应用",
    "configuring-mcp-client": "配置 MCP 客户端",
    "creating-app": "创建 BrowserMCP 应用",
    tools: "工具",
    resources: "资源",
    prompts: "提示词",
    "results-errors": "结果与错误",
    "connection-lifecycle": "连接生命周期",
    "bridge-protocol": "Bridge 协议",
    "security-model": "安全模型",
    "multiple-applications": "多个应用",
    "api-reference": "API 参考",
    "bridge-configuration": "Bridge 配置",
    troubleshooting: "故障排除",
    development: "开发",
    roadmap: "路线图",
  },
  es: {
    introduction: "Introducción",
    architecture: "Arquitectura",
    "getting-started": "Primeros pasos",
    "installing-macos-bridge": "Instalar el Bridge y la app para macOS",
    "configuring-mcp-client": "Configurar un cliente MCP",
    "creating-app": "Crear una aplicación BrowserMCP",
    tools: "Herramientas",
    resources: "Recursos",
    prompts: "Prompts",
    "results-errors": "Resultados y errores",
    "connection-lifecycle": "Ciclo de vida de la conexión",
    "bridge-protocol": "Protocolo del Bridge",
    "security-model": "Modelo de seguridad",
    "multiple-applications": "Múltiples aplicaciones",
    "api-reference": "Referencia de API",
    "bridge-configuration": "Configuración del Bridge",
    troubleshooting: "Solución de problemas",
    development: "Desarrollo",
    roadmap: "Hoja de ruta",
  },
  hi: {
    introduction: "परिचय",
    architecture: "आर्किटेक्चर",
    "getting-started": "शुरुआत",
    "installing-macos-bridge": "Bridge और macOS ऐप इंस्टॉल करना",
    "configuring-mcp-client": "MCP क्लाइंट कॉन्फ़िगर करना",
    "creating-app": "BrowserMCP ऐप बनाना",
    tools: "टूल",
    resources: "रिसोर्स",
    prompts: "प्रॉम्प्ट",
    "results-errors": "परिणाम और त्रुटियाँ",
    "connection-lifecycle": "कनेक्शन जीवनचक्र",
    "bridge-protocol": "Bridge प्रोटोकॉल",
    "security-model": "सुरक्षा मॉडल",
    "multiple-applications": "एकाधिक ऐप",
    "api-reference": "API संदर्भ",
    "bridge-configuration": "Bridge कॉन्फ़िगरेशन",
    troubleshooting: "समस्या निवारण",
    development: "विकास",
    roadmap: "रोडमैप",
  },
  ar: {
    introduction: "مقدمة",
    architecture: "البنية",
    "getting-started": "البدء",
    "installing-macos-bridge": "تثبيت Bridge وتطبيق macOS",
    "configuring-mcp-client": "إعداد عميل MCP",
    "creating-app": "إنشاء تطبيق BrowserMCP",
    tools: "الأدوات",
    resources: "الموارد",
    prompts: "المطالبات",
    "results-errors": "النتائج والأخطاء",
    "connection-lifecycle": "دورة حياة الاتصال",
    "bridge-protocol": "بروتوكول Bridge",
    "security-model": "نموذج الأمان",
    "multiple-applications": "تطبيقات متعددة",
    "api-reference": "مرجع API",
    "bridge-configuration": "إعداد Bridge",
    troubleshooting: "استكشاف الأخطاء",
    development: "التطوير",
    roadmap: "خارطة الطريق",
  },
  "pt-BR": {
    introduction: "Introdução",
    architecture: "Arquitetura",
    "getting-started": "Primeiros passos",
    "installing-macos-bridge": "Instalar o Bridge e o app para macOS",
    "configuring-mcp-client": "Configurar um cliente MCP",
    "creating-app": "Criar um app BrowserMCP",
    tools: "Ferramentas",
    resources: "Recursos",
    prompts: "Prompts",
    "results-errors": "Resultados e erros",
    "connection-lifecycle": "Ciclo de vida da conexão",
    "bridge-protocol": "Protocolo do Bridge",
    "security-model": "Modelo de segurança",
    "multiple-applications": "Múltiplos aplicativos",
    "api-reference": "Referência da API",
    "bridge-configuration": "Configuração do Bridge",
    troubleshooting: "Solução de problemas",
    development: "Desenvolvimento",
    roadmap: "Roteiro",
  },
  bn: {
    introduction: "পরিচিতি",
    architecture: "আর্কিটেকচার",
    "getting-started": "শুরু করুন",
    "installing-macos-bridge": "Bridge ও macOS অ্যাপ ইনস্টল",
    "configuring-mcp-client": "MCP ক্লায়েন্ট কনফিগারেশন",
    "creating-app": "BrowserMCP অ্যাপ তৈরি",
    tools: "টুল",
    resources: "রিসোর্স",
    prompts: "প্রম্পট",
    "results-errors": "ফলাফল ও ত্রুটি",
    "connection-lifecycle": "সংযোগ জীবনচক্র",
    "bridge-protocol": "Bridge প্রোটোকল",
    "security-model": "নিরাপত্তা মডেল",
    "multiple-applications": "একাধিক অ্যাপ",
    "api-reference": "API রেফারেন্স",
    "bridge-configuration": "Bridge কনফিগারেশন",
    troubleshooting: "সমস্যা সমাধান",
    development: "ডেভেলপমেন্ট",
    roadmap: "রোডম্যাপ",
  },
  ru: {
    introduction: "Введение",
    architecture: "Архитектура",
    "getting-started": "Начало работы",
    "installing-macos-bridge": "Установка Bridge и приложения macOS",
    "configuring-mcp-client": "Настройка клиента MCP",
    "creating-app": "Создание приложения BrowserMCP",
    tools: "Инструменты",
    resources: "Ресурсы",
    prompts: "Промпты",
    "results-errors": "Результаты и ошибки",
    "connection-lifecycle": "Жизненный цикл подключения",
    "bridge-protocol": "Протокол Bridge",
    "security-model": "Модель безопасности",
    "multiple-applications": "Несколько приложений",
    "api-reference": "Справочник API",
    "bridge-configuration": "Настройка Bridge",
    troubleshooting: "Диагностика",
    development: "Разработка",
    roadmap: "Планы",
  },
};

const localizedSectionTitles: Partial<Record<SupportedLocale, Readonly<Record<string, string>>>> = {
  ja: japaneseSectionTitles,
};

export const localizedDocPageTitle = (
  locale: SupportedLocale,
  pageId: string,
  fallback: string,
): string => localizedPageTitles[locale]?.[pageId] ?? fallback;

export const localizedDocSectionTitle = (
  locale: SupportedLocale,
  pageId: string,
  sectionId: string,
  fallback: string,
): string => localizedSectionTitles[locale]?.[`${pageId}/${sectionId}`] ?? fallback;
